import { io } from 'socket.io-client';
import { config, getAuthToken } from './bridge.js';

const LOBBY = 'teenpatti';

let socket = null;
let subscribed = false;
const listeners = {};
const lifecycleHandlers = { connect: [], disconnect: [], reconnect: [] };

export function onSocketLifecycle(kind, fn) {
  if (!lifecycleHandlers[kind]) return () => {};
  lifecycleHandlers[kind].push(fn);
  return () => {
    const arr = lifecycleHandlers[kind];
    const i = arr.indexOf(fn);
    if (i >= 0) arr.splice(i, 1);
  };
}

function fireLifecycle(kind, info) {
  for (const fn of lifecycleHandlers[kind]) {
    try { fn(info); } catch (e) {}
  }
}

export async function connectSocket() {
  if (socket) {
    // Reuse the one socket: a second io() here used to leave the first one
    // alive, still delivering (or no longer delivering) into stale handlers.
    if (!socket.connected) {
      try { socket.connect(); } catch (_) {}
    }
    return socket;
  }
  const token = await getAuthToken();
  if (socket) return socket; // lost a race with a concurrent connectSocket()
  const url = (config.socketUrl || config.backendUrl).replace(/\/$/, '');
  socket = io(url, {
    transports: ['websocket'],
    auth: token ? { token } : {},
    reconnection: true,
    reconnectionDelay: 1000,
  });
  // `connect` also fires after every automatic reconnect, which is what
  // re-joins the lobby room (the server forgets room membership on disconnect).
  socket.on('connect', () => {
    if (subscribed) socket.emit(`${LOBBY}_lobby_subscribe`);
    fireLifecycle('connect');
  });
  // socket.io v4 emits `reconnect` on the Manager, not on the socket.
  socket.io.on('reconnect', () => fireLifecycle('reconnect'));
  socket.on('disconnect', (reason) => fireLifecycle('disconnect', { reason }));
  for (const event of Object.keys(listeners)) attachDispatcher(event);
  return socket;
}

/**
 * One dispatcher per event on the socket, fanning out to `listeners[event]`.
 * Handlers are never attached to the socket directly, so a handler registered
 * after connect is not called twice.
 */
const attached = new Set();
function attachDispatcher(event) {
  if (!socket || attached.has(event)) return;
  attached.add(event);
  socket.on(event, (payload) => {
    const arr = listeners[event];
    if (!arr) return;
    for (const fn of [...arr]) {
      try { fn(payload); } catch (_) {}
    }
  });
}

/**
 * Make sure we are connected and in the lobby room. Safe to call often — the
 * resync watchdog calls it whenever the table looks stale.
 */
export function ensureSubscribed() {
  subscribed = true;
  if (!socket) {
    connectSocket().catch(() => {});
    return;
  }
  if (socket.connected) {
    socket.emit(`${LOBBY}_lobby_subscribe`);
  } else {
    try { socket.connect(); } catch (_) {}
  }
}

/** Join the Teen Patti lobby room; the server replies with a state snapshot. */
export function subscribeLobby() {
  subscribed = true;
  if (socket && socket.connected) socket.emit(`${LOBBY}_lobby_subscribe`);
}

export function unsubscribeLobby() {
  subscribed = false;
  if (socket && socket.connected) {
    try { socket.emit(`${LOBBY}_lobby_unsubscribe`); } catch (_) {}
  }
}

export function on(event, handler) {
  listeners[event] = listeners[event] || [];
  listeners[event].push(handler);
  attachDispatcher(event);
  return () => off(event, handler);
}

export function off(event, handler) {
  const arr = listeners[event];
  if (!arr) return;
  const i = arr.indexOf(handler);
  if (i >= 0) arr.splice(i, 1);
}

export function isConnected() { return !!(socket && socket.connected); }

/** Tear down for the Flutter cleanup hook. */
export function disconnectSocket() {
  unsubscribeLobby();
  if (socket) {
    try { socket.removeAllListeners(); } catch (_) {}
    try { socket.disconnect(); } catch (_) {}
  }
  socket = null;
  attached.clear();
  for (const key of Object.keys(listeners)) delete listeners[key];
}
